import * as React from 'react';
import PrimaryMailFolderTreeContainer from './PrimaryMailFolderTreeContainer';
import {
    ArchiveMailFolderTreeContainer,
    SharedFolderTreeParentContainer,
} from './lazyFolderComponents';
import { observer } from 'mobx-react-lite';
import { LazyFolderPermissionsDialog } from 'owa-mail-folder-permissions';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { FolderHierarchyConfigurationQueryDocument } from '../graphql/__generated__/FolderHierarchyConfigurationQuery.interface';
import { useManagedQuery } from 'owa-apollo-hooks/lib/useManagedQuery';
import type { MailboxType } from 'owa-graph-schema';
import type { MailboxInfo } from 'owa-client-types';

export interface MailFolderTreesParentContainerProps {
    isFavoriteOrGroupListShown: boolean;
    leftNavStyleWithSectionHeaders: string;
    leftNavStyleWithNoSectionHeaders: string;
    setSize: number;
    positionInSet: number;
    primarySmtp?: string;
    showRootNode?: boolean;
}

/**
 * Parent container component hosting primary, archive and shared folder tree containers
 */
export default observer(function MailFolderTreesParentContainer(
    props: MailFolderTreesParentContainerProps
) {
    const {
        leftNavStyleWithSectionHeaders,
        leftNavStyleWithNoSectionHeaders,
        isFavoriteOrGroupListShown,
        primarySmtp,
        showRootNode,
        setSize,
    } = props;

    let { positionInSet } = props;

    // Primary root node should be shown in case of any one of the Favorites or Groups or Archive mailbox or Shared Folders is shown
    const shouldShowPrimaryRootNode = showRootNode ?? (isFavoriteOrGroupListShown || !isConsumer());
    const { loading, data } = useManagedQuery(FolderHierarchyConfigurationQueryDocument);

    if (loading) {
        return null;
    }

    if (!data) {
        return null;
    }

    const userConfig = data.userConfiguration;
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2533 (58,26): Object is possibly 'null' or 'undefined'.
    // @ts-expect-error
    const userIdentity = primarySmtp ?? userConfig.SessionSettings?.LogonEmailAddress;
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2533 (62,36): Object is possibly 'null' or 'undefined'.
    // @ts-expect-error
    const archiveRootDisplayName = userConfig.SessionSettings?.ArchiveDisplayName;
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2533 (66,32): Object is possibly 'null' or 'undefined'.
    // @ts-expect-error
    const archiveMailboxGuid = userConfig.SessionSettings?.ArchiveMailboxGuid;
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2533 (70,32): Object is possibly 'null' or 'undefined'.
    // @ts-expect-error
    const organizationDomain = userConfig.SessionSettings?.OrganizationDomain;
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2533 (74,29): Object is possibly 'null' or 'undefined'.
    // @ts-expect-error
    const isShadowMailbox = userConfig.SessionSettings?.IsShadowMailbox;
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2533 (78,24): Object is possibly 'null' or 'undefined'.
    // @ts-expect-error
    const hasArchive = userConfig.SessionSettings?.HasArchive;
    const shouldShowArchiveTree =
        hasArchive &&
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2533 (84,9): Object is possibly 'null' or 'undefined'.
        // @ts-expect-error
        userConfig.PolicySettings?.ShowOnlineArchiveEnabled &&
        archiveRootDisplayName != null;
    const mailboxType: MailboxType = 'UserMailbox';
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2533 (90,32): Object is possibly 'null' or 'undefined'.
    // @ts-expect-error
    const mailboxSmtpAddress = primarySmtp ?? userConfig.SessionSettings?.UserEmailAddress;
    const primaryMailboxInfo: MailboxInfo = {
        type: mailboxType,
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (96,9): Type 'Maybe<string> | undefined' is not assignable to type 'string'.
        // @ts-expect-error
        userIdentity: userIdentity,
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (100,9): Type 'Maybe<string> | undefined' is not assignable to type 'string'.
        // @ts-expect-error
        mailboxSmtpAddress: mailboxSmtpAddress,
    };

    return (
        <>
            <PrimaryMailFolderTreeContainer
                showRootNode={shouldShowPrimaryRootNode}
                className={
                    shouldShowPrimaryRootNode
                        ? leftNavStyleWithSectionHeaders
                        : leftNavStyleWithNoSectionHeaders
                }
                setSize={setSize}
                positionInSet={positionInSet}
                userConfiguration={userConfig}
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2322 (118,17): Type 'boolean | null | undefined' is not assignable to type 'boolean'.
                // @ts-expect-error
                isShadowMailbox={isShadowMailbox}
                mailboxInfo={primaryMailboxInfo}
            />
            {
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2322 (122,29): Type 'Maybe<string> | undefined' is not assignable to type 'string'.
                // @ts-expect-error
                !isConsumer(userIdentity) && (
                    <>
                        {shouldShowArchiveTree && (
                            <ArchiveMailFolderTreeContainer
                                className={leftNavStyleWithSectionHeaders}
                                setSize={setSize}
                                positionInSet={++positionInSet}
                                archiveRootDisplayName={archiveRootDisplayName}
                                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                                // -> Error TS2322 (132,29): Type 'Maybe<string> | undefined' is not assignable to type 'string'.
                                // @ts-expect-error
                                userIdentity={userIdentity}
                                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                                // -> Error TS2322 (136,29): Type 'Maybe<string> | undefined' is not assignable to type 'string'.
                                // @ts-expect-error
                                archiveMailboxGuid={archiveMailboxGuid}
                                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                                // -> Error TS2322 (140,29): Type 'Maybe<string> | undefined' is not assignable to type 'string'.
                                // @ts-expect-error
                                organizationDomain={organizationDomain}
                                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                                // -> Error TS2322 (144,29): Type 'boolean | null | undefined' is not assignable to type 'boolean'.
                                // @ts-expect-error
                                isShadowMailbox={isShadowMailbox}
                                primaryMailboxInfo={primaryMailboxInfo}
                                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                                // -> Error TS2322 (149,29): Type 'Maybe<string> | undefined' is not assignable to type 'string'.
                                // @ts-expect-error
                                userEmailAddress={userConfig.SessionSettings?.UserEmailAddress}
                            />
                        )}
                        <SharedFolderTreeParentContainer
                            className={leftNavStyleWithSectionHeaders}
                            setSize={setSize}
                            positionInSet={++positionInSet}
                            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                            // -> Error TS2322 (159,25): Type 'Maybe<string> | undefined' is not assignable to type 'string'.
                            // @ts-expect-error
                            userIdentity={userIdentity}
                            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                            // -> Error TS2322 (163,25): Type 'boolean | null | undefined' is not assignable to type 'boolean'.
                            // @ts-expect-error
                            isShadowMailbox={isShadowMailbox}
                            primaryMailboxInfo={primaryMailboxInfo}
                        />
                        <LazyFolderPermissionsDialog />
                    </>
                )
            }
        </>
    );
});
